/**
 * 受け渡しの「止まらずに移る / 残る / 外れる」の 3 つの一覧と、入れ直しを勧める物の名前（2026-10-21 core/handoff-1a）。
 * 渡す画面（pages/TransferPage.tsx）と受け取る画面（pages/TransferIncomingPage.tsx）で同じ部品 = 両方の人が同じ一覧を見る。
 * 中身（どのキーを出すか）はサーバーの handoff（projectTransfer.ts の HANDOFF_ITEMS）。文は projectTransfer.ts の itemKeys で引く（知らないキーは出さない）。
 * side: 'owner' = 渡す人（「あなたに残るもの」）/ 'recipient' = 受け取る人（「今の持ち主に残るもの」）。
 * 名前（Functions の値の名前・ドメイン）は利用者が付けた値 = React のテキストとして出す（HTML にしない）
 */
import { useT, type MessageKey } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { itemKeys, type HandoffDetails, type HandoffItems } from '../projectTransfer';

export function HandoffLists({ handoff, details, side }: { handoff: HandoffItems; details: HandoffDetails | null; side: 'owner' | 'recipient' }) {
  const { t } = useT();
  // 売上と振込先の行は、出金を開けている時だけ（初回の公開では閉じている = launchHidden.ts の payouts）。閉じている間は利用料だけの文・行を出さない
  const earn: Partial<Record<string, MessageKey>> = isLaunchHidden('payouts')
    ? {}
    : { future_earnings: 'transfer.item.future_earnings', earnings_before: 'transfer.item.earnings_before', payout_account: 'transfer.item.payout_account' };
  const groups = [
    { title: t('transfer.moves.title'), keys: itemKeys('moves', handoff.moves, earn) },
    { title: t(side === 'owner' ? 'transfer.stays.title' : 'transfer.stays.titleIncoming'), keys: itemKeys('stays', handoff.stays, earn) },
    { title: t('transfer.redo.title'), keys: itemKeys('redo', handoff.redo) }
  ];
  const names = details
    ? [
        details.functionSecretNames.length ? t('transfer.details.secrets', { names: details.functionSecretNames.join(', ') }) : null,
        details.signInProviders.length ? t('transfer.details.signIn', { names: details.signInProviders.join(', ') }) : null,
        details.customDomains.length ? t('transfer.details.domains', { names: details.customDomains.join(', ') }) : null,
        details.storeKeys.length ? t('transfer.details.storeKeys', { names: details.storeKeys.join(', ') }) : null
      ].filter((x): x is string => !!x)
    : [];
  return (
    <section className="card">
      <h2 className="card-title">{t(side === 'owner' ? 'transfer.whatTitle' : 'transfer.whatTitleIncoming')}</h2>
      <div className="handoff-lists">
        {groups.map((g) => (
          <div key={g.title}>
            <h3>{g.title}</h3>
            <ul>
              {g.keys.map((k) => (
                <li key={k}>{t(k)}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {names.length > 0 && (
        <>
          <h3>{t('transfer.details.title')}</h3>
          <ul>
            {names.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
